import { Table, TableBody, TableCaption, TableCell, TableRow } from '@/components/ui/table';
import { DataList, Text, Tooltip } from '@radix-ui/themes';
import clsx from 'clsx';
import CopyableID from 'components/CopyableID';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import HubertPopover from 'components/HubertUI/HubertPopover';
import { HubertSlider } from 'components/HubertUI/HubertSlider';
import IsAI from 'components/IsAi';
import { usePreferredQuestionLanguage } from 'hooks/usePreferredQuestionLanguage';
import { CheckIcon, Filter, X } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { type TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import type { TChatCollection, TInterviewScoreTableDetail } from 'types/scores.types';
import type { ScoreInterviewContextType } from '../../scoring.context';
import { useScoreInterview } from '../../scoring.context';

interface ScoreTableDetailProps extends ComponentProps<'div'> {
  collection: TChatCollection;
  detail: TInterviewScoreTableDetail;
}

function ConsideredQuestionsPopover(collection: TChatCollection, detail: TInterviewScoreTableDetail) {
  const mainContent = document.getElementById('main-content') as HTMLElement;
  const question = collection.question;

  const currentId = detail.categoryId ? detail.categoryId : detail.formId ? detail.formId : detail.questionId;

  if (!question) {
    return <div className="bg-danger-50">Question is missing</div>;
  }

  if (question.type !== 'open') {
    return <HubertBadge variant={'secondary'}>{currentId}</HubertBadge>;
  }

  function buildQuestionListItem(label: string, list: string[]) {
    if (!list.length) return null;
    return (
      <DataList.Item>
        <DataList.Label minWidth="88px">{label}</DataList.Label>
        <DataList.Value className="flex flex-col justify-between gap-2">
          {list.map((el) => {
            return <CopyableID key={el} id={el} showCopyButton={true} />;
          })}
        </DataList.Value>
      </DataList.Item>
    );
  }

  return (
    <HubertPopover>
      <HubertPopover.Trigger>
        <HubertBadge variant={'secondary'}>{currentId}</HubertBadge>
      </HubertPopover.Trigger>
      <HubertPopover.Content
        portalContent={true}
        portalElement={mainContent}
        className={clsx('flex flex-col justify-between gap-4')}
      >
        <DataList.Root>
          <DataList.Item align="center">
            <DataList.Label minWidth="88px">Question Id</DataList.Label>
            <DataList.Value>{question?.id}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label minWidth="88px">{detail.categoryId ? 'Category ID' : 'Score Id'}</DataList.Label>
            <DataList.Value>{currentId}</DataList.Value>
          </DataList.Item>

          {buildQuestionListItem('Question IDs scored', detail.utilizedQuestionIds ?? [])}
          {buildQuestionListItem('Required Question IDs', detail.requiredQuestionIds ?? [])}
          {buildQuestionListItem('Optional Question IDs', detail.optionalQuestionIds ?? [])}
        </DataList.Root>
      </HubertPopover.Content>
    </HubertPopover>
  );
}

const ScoreTableDetail = ({ collection, detail, ...props }: ScoreTableDetailProps) => {
  const { updateScoreData, preferredLanguage, setChatFilter, chatFilter, resetChatFilter } =
    useScoreInterview() as ScoreInterviewContextType;
  const { questionText } = usePreferredQuestionLanguage(
    collection.questionId,
    collection.question,
    collection.expectedLanguage[0] as TAvailableLanguageCodes,
    // @ts-ignore
    preferredLanguage,
  );

  const question = collection.question;
  const questionId = collection.questionId;
  const currentId = detail.categoryId ? detail.categoryId : detail.formId ? detail.formId : detail.questionId;
  const [_, setValue] = useState([detail.value! >= 0 ? detail.value : 0]);

  if (!question) {
    return <div className="bg-danger-50">Question is missing</div>;
  }

  const filterAnswers = (keepFiltered?: boolean) => {
    if (detail.questionId === 'bonus_booster') return;

    const handleFilter = (filterId: string, questionIds: string[]) => {
      if (chatFilter.isFiltered && chatFilter.filterId === filterId) {
        if (keepFiltered) return;
        resetChatFilter();
      } else {
        setChatFilter({
          isFiltered: true,
          filterId: filterId,
          questionIds: questionIds,
        });
      }
    };

    if (detail.type === 'bonus') {
      handleFilter(questionId, [questionId]);

      return;
    }

    if (question.type === 'open') {
      const utilized = detail.utilizedQuestionIds ?? [];
      const required = detail.requiredQuestionIds ?? [];
      const optional = detail.optionalQuestionIds ?? [];
      const allIds = [...new Set(utilized.concat(required).concat(optional))];
      handleFilter(currentId, allIds);
    } else {
      handleFilter(questionId, [questionId]);
      // resetChatFilter(scrollTo);
    }
  };

  return (
    <div
      {...props}
      className={clsx(
        'flex flex-col justify-between gap-1',
        'bg-primary-50',
        'rounded-md',
        'w-full',
        'p-2',
        chatFilter.isFiltered && chatFilter.filterId === currentId ? 'border-primary-800 border-2' : '',
      )}
    >
      <div className={clsx('w-full', 'flex flex-row justify-between gap-2')}>
        <Tooltip content={`${detail.categoryId ? 'Filter chat' : 'Scroll to'}`}>
          <Filter
            className={clsx(
              detail.questionId === 'bonus_booster' ? 'hidden' : '',
              'hover:text-primary-500 cursor-pointer',
              chatFilter.isFiltered && chatFilter.filterId === currentId ? 'text-primary-500' : '',
            )}
            onClick={() => filterAnswers()}
          />
        </Tooltip>
        {ConsideredQuestionsPopover(collection, detail)}
        <IsAI aiDetection={detail.aiDetection} />

        {detail.validationTags && detail.validationTags.length
          ? detail.validationTags.map((tag, index) => {
              return (
                <HubertBadge
                  key={`validation-tag-${detail.questionId}-${index}`}
                  variant={tag === 'PASSED' ? 'success' : 'warning'}
                >
                  {tag.split('_').join(' ')}
                </HubertBadge>
              );
            })
          : null}

        <HubertPopover>
          <HubertPopover.Trigger>
            <HubertBadge variant={detail.type === 'bonus' ? 'bonus' : 'default'}>{detail.type}</HubertBadge>
          </HubertPopover.Trigger>
          <HubertPopover.Content>
            <Table>
              <TableCaption>Score detail data</TableCaption>
              <TableBody>
                <TableRow>
                  <TableCell>questionId</TableCell>
                  <TableCell className="text-right">{detail.questionId}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>categoryId</TableCell>
                  <TableCell className="text-right">{detail.categoryId ?? ''}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>formId</TableCell>
                  <TableCell className="text-right">{detail.formId ?? ''}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>type</TableCell>
                  <TableCell className="text-right">{detail.type}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>scale</TableCell>
                  <TableCell className="text-right">
                    [{detail.scale[0]}, {detail.scale[1]}]
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>step</TableCell>
                  <TableCell className="text-right">{detail.step}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>value</TableCell>
                  <TableCell className="text-right">{detail.value}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>score</TableCell>
                  <TableCell className="text-right">{detail.score}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>predicted</TableCell>
                  <TableCell className="text-right">{detail.predicted}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>displayedScore</TableCell>
                  <TableCell className="text-right">{detail.displayedScore}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>displayed_score</TableCell>
                  <TableCell className="text-right">{detail.displayed_score}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>weight</TableCell>
                  <TableCell className="text-right">{detail.weight ?? ''}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>edited</TableCell>
                  <TableCell className="flex justify-end text-right">{detail.edited ? <CheckIcon /> : <X />}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>aiDetection - is AI</TableCell>
                  <TableCell className="flex justify-end text-right">
                    {detail.aiDetection?.isAi ? <CheckIcon /> : <X />}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>aiDetection - score</TableCell>
                  <TableCell className="text-right">{detail.aiDetection?.score}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>aiDetection - tag</TableCell>
                  <TableCell className="text-right">{detail.aiDetection?.tag}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </HubertPopover.Content>
        </HubertPopover>
      </div>

      <div className="m-1 p-1 text-xs" dangerouslySetInnerHTML={{ __html: questionText }} />

      <div className="hubert-vertical-center flex w-full flex-row justify-between">
        <div className="hubert-vertical-center flex flex-row justify-between gap-2">
          <Text
            as="div"
            className={clsx(
              'flex items-center justify-center',
              'bg-primary-300',
              'text-sm font-semibold text-white',
              'h-8 w-8',
              'rounded-full',
              'm-1',
              'p-2',
            )}
          >
            {/* {(Math.round(detail.value * 10) / 10)} */}
            {Math.round(detail.displayedScore * 10) / 10}
          </Text>

          <Text
            as="div"
            className={clsx(
              'flex items-center justify-center',
              'bg-info-300',
              'text-sm font-semibold text-white',
              'h-8 w-8',
              'rounded-full',
              'm-1',
              'p-2',
            )}
          >
            {/* {(Math.round(detail.value * 10) / 10)} */}
            {Math.round((detail.score ?? 0) * 10) / 10}
          </Text>
        </div>

        <div className="mr-2 ml-2">
          <HubertSlider
            onClick={() => {
              filterAnswers(true);
            }}
            onValueChange={(v) => {
              setValue(v);
              if (v && Array.isArray(v) && v.length) {
                updateScoreData(collection.questionId, v[0], detail.formId, detail.categoryId);
              }
            }}
            min={detail.scale[0]}
            max={detail.scale[1]}
            step={detail.step}
            value={[detail.value]}
          />
        </div>
      </div>
    </div>
  );
};

export default ScoreTableDetail;
